import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight, ChevronLeft, FolderOpen, BookOpen } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { pluralizeLevel, getChildLevel, formatNodeTitle } from '@/lib/pluralize'

export default function LawTreePage() {
  const { lawCode } = useParams<{ lawCode: string }>()
  const [searchParams] = useSearchParams()
  const parentId = searchParams.get('parent') || undefined
  const parentTitle = searchParams.get('title') || undefined
  const parentParentTitle = searchParams.get('from') || undefined
  const navigate = useNavigate()

  const { data: lawTypes } = useQuery({
    queryKey: ['lawTypes'],
    queryFn: () => apiClient.getLawTypes(),
    staleTime: Infinity,
  })

  const { data: tree, isLoading, error } = useQuery({
    queryKey: ['lawTree', lawCode, parentId],
    queryFn: () => apiClient.getLawTree(lawCode!, parentId),
    enabled: !!lawCode,
  })

  const currentLawType = lawTypes?.find(lt => lt.code === lawCode)
  const hierarchy = currentLawType?.hierarchy || []
  const isRoot = !parentId
  const headerTitle = parentTitle || (currentLawType ? `${lawCode} ${currentLawType.name}` : lawCode)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-telegram-button/30 border-t-telegram-button" />
          <span className="text-sm text-telegram-hint">Загрузка...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-telegram-link mb-4 active:opacity-60 transition-opacity">
          <ChevronLeft size={20} />
          <span className="text-sm font-medium">Назад</span>
        </button>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="text-sm text-telegram-hint">Ошибка загрузки данных</p>
        </div>
      </div>
    )
  }

  return (
    <div className="tree-page flex flex-col min-h-full">
      {/* Header with breadcrumb — Liquid Glass */}
      <div className="tree-header sticky top-0 z-10 glass-header px-4 py-2.5">
        <button
          onClick={() => navigate(isRoot ? '/' : -1 as any)}
          className="flex items-center gap-1 active:opacity-60 transition-opacity"
          style={{ color: 'var(--tree-link-color)' }}
        >
          <ChevronLeft size={16} />
          <span className="text-xs font-medium line-clamp-1">
            {isRoot ? 'Главная' : (parentParentTitle || lawCode)}
          </span>
        </button>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="w-7 h-7 rounded-xl glass-icon flex items-center justify-center shrink-0">
            <BookOpen size={14} className="text-telegram-button" />
          </div>
          <h1 className="text-sm font-semibold leading-tight line-clamp-2">{headerTitle}</h1>
        </div>
      </div>

      {/* Tree list */}
      <div className="tree-content flex-1 px-3 py-2.5">
        <div className="tree-list space-y-2">
          {tree?.map((node, index) => (
            <button
              key={node.id}
              onClick={() => {
                if (node.has_children) {
                  const nodeDisplayTitle = formatNodeTitle(node.level, node.num, node.title)
                  const params = new URLSearchParams({
                    parent: node.id,
                    title: nodeDisplayTitle,
                    from: headerTitle!,
                  })
                  navigate(`/law/${lawCode}?${params}`)
                } else {
                  navigate(`/law/${lawCode}/article/${node.id}`)
                }
              }}
              className="tree-item glass-card w-full flex items-center gap-2.5 p-3 glass-appear"
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <div className="flex-1 text-left min-w-0 relative z-10">
                <div className="text-sm font-medium leading-snug line-clamp-2">{formatNodeTitle(node.level, node.num, node.title)}</div>
                {node.subtitle && (
                  <div className="text-xs text-telegram-hint mt-0.5 line-clamp-1">{node.subtitle}</div>
                )}
                {node.has_children && node.children_count > 0 && (
                  <div className="text-[10px] text-telegram-hint/70 mt-0.5">
                    {pluralizeLevel(node.children_count, getChildLevel(node.level, hierarchy) || node.level)}
                  </div>
                )}
              </div>
              {node.has_children && (
                <ChevronRight className="text-telegram-hint/40 shrink-0 relative z-10" size={16} />
              )}
            </button>
          ))}
        </div>

        {(!tree || tree.length === 0) && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full glass-icon flex items-center justify-center mb-4">
              <FolderOpen size={28} className="text-telegram-hint/50" />
            </div>
            <p className="text-sm text-telegram-hint">Нет данных для отображения</p>
          </div>
        )}
      </div>
    </div>
  )
}
